import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { auth as authApi, type User } from "@/lib/api";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, company?: string) => Promise<void>;
  logout: () => void;
  /** Set user directly after OTP verification (bypasses login flow) */
  setUserFromVerification: (user: User) => void;
  /** Email pending verification (set after register or login 403) */
  pendingVerificationEmail: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export class VerificationRequiredError extends Error {
  email: string;
  constructor(email: string) {
    super("Email verification required");
    this.email = email;
    this.name = "VerificationRequiredError";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem("moremail_token");
    if (token) {
      authApi
        .me()
        .then((res) => setUser(res.user))
        .catch(() => localStorage.removeItem("moremail_token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await authApi.login({ email, password });
      localStorage.setItem("moremail_token", res.token);
      setUser(res.user);
    } catch (err: any) {
      // Check if the API returned requiresVerification
      if (err?.data?.requiresVerification) {
        setPendingVerificationEmail(err.data.email || email);
        throw new VerificationRequiredError(err.data.email || email);
      }
      throw err;
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name: string, company?: string) => {
    const res = await authApi.register({ email, password, name, company });
    localStorage.setItem("moremail_token", res.token);
    // Don't set user yet — they need to verify
    if ((res as any).requiresVerification) {
      setPendingVerificationEmail(email);
      throw new VerificationRequiredError(email);
    }
    setUser(res.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("moremail_token");
    setUser(null);
    setPendingVerificationEmail(null);
  }, []);

  const setUserFromVerification = useCallback((verifiedUser: User) => {
    setUser(verifiedUser);
    setPendingVerificationEmail(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      setUserFromVerification, pendingVerificationEmail,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
